/** Минимальная длина значения, которое считается сканом аппаратного сканера. */
export const DEFAULT_MIN_BARCODE_LENGTH = 10;

/** Таймаут между символами скана в миллисекундах. */
export const DEFAULT_SCAN_TIMEOUT_MS = 200;

/** Параметры keyboard-wedge adapter для аппаратного сканера. */
export interface KeyboardScannerAdapterOptions {
    /** Минимальная длина скана. */
    minBarcodeLength?: number;
    /** Таймаут между символами скана в миллисекундах. */
    scanTimeoutMs?: number;
    /**
     * Проверяет, можно ли сейчас перехватывать ввод.
     * @param event событие клавиатуры
     */
    canCapture?: (event: KeyboardEvent) => boolean;
    /**
     * Вызывается после завершения валидного скана клавишей `Enter`.
     * @param barcode отсканированное значение
     */
    onScan: (barcode: string) => void;
}

/**
 * Подписывает приложение на ввод аппаратного сканера в режиме keyboard wedge.
 * @param options настройки распознавания скана и callback результата
 * @returns cleanup-функция document listener и pending timeout
 */
export function attachKeyboardScanner(options: KeyboardScannerAdapterOptions): () => void {
    const minBarcodeLength = options.minBarcodeLength ?? DEFAULT_MIN_BARCODE_LENGTH;
    const scanTimeoutMs = options.scanTimeoutMs ?? DEFAULT_SCAN_TIMEOUT_MS;
    let currentCode = "";
    let resetTimerId: number | null = null;

    const reset = (): void => {
        currentCode = "";
        if (resetTimerId != null) {
            window.clearTimeout(resetTimerId);
            resetTimerId = null;
        }
    };

    const scheduleReset = (): void => {
        if (resetTimerId != null) {
            window.clearTimeout(resetTimerId);
        }

        resetTimerId = window.setTimeout(reset, scanTimeoutMs);
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
        if (options.canCapture?.(event) === false) {
            reset();
            return;
        }

        if (event.key === "Enter") {
            if (currentCode.length >= minBarcodeLength) {
                event.preventDefault();
                options.onScan(currentCode);
            }

            reset();
            return;
        }

        if (event.key.length !== 1 || event.ctrlKey || event.altKey || event.metaKey) {
            return;
        }

        currentCode += event.key;
        scheduleReset();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
        document.removeEventListener("keydown", handleKeyDown);
        reset();
    };
}
